import { useCallback, useMemo, useState } from 'react'

import { Button, Divider, Flex, Group, Select, SimpleGrid, Stack, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import produce from 'immer'
import { useGetOne, useTranslate } from 'react-admin'
import { YoutubePlaylistAttributesApiDTO } from '../../../../codegen/generated/Api.schemas'
import { useGetApiVideoPromotionChannelsYoutubeChannelIdPlaylists } from '../../../../codegen/generated/video-promotion'
import { VideoPromotionHelper } from '../../../../helpers/VideoPromotion.helper'
import { useVideoPromotionStore } from '../../../../stores/useVideoPromotionStore'
import { YoutubeChannel } from '../../../../types/YoutubeChannel'
import { IInfocardTypes } from '../../../../types/components/VideoPromotion.type'
import { PromotionTargetVideoSelect } from '../PromotionTargetVideoSelect'

type IEditTarget = {
  playlistId?: YoutubePlaylistAttributesApiDTO['id']
  videoId?: string
  videoTitle?: string
}

type EditInfocardTargetProps = {
  infocardId: string | undefined
  onClose: () => void
}

export const EditInfocardTarget = ({ infocardId, onClose }: EditInfocardTargetProps) => {
  const t = useTranslate()
  const videoPromotionStore = useVideoPromotionStore()
  const infocard = videoPromotionStore.infoCardsDraft.find(ic => ic.id === infocardId)

  const [selectedType, setSelectedType] = useState<IInfocardTypes>(infocard?.target.type ?? 'video')

  const form = useForm<IEditTarget>({
    initialValues: {
      playlistId: infocard?.target.type === 'playlist' ? infocard.target.targetId : undefined,
      videoId: infocard?.target.type === 'video' ? infocard.target.targetId : undefined,
      videoTitle: infocard?.target.type === 'video' ? infocard.target.title : undefined,
    },
  })

  const youtubeChannelQuery = useGetOne<YoutubeChannel>(
    'youtubeChannels',
    {
      id: videoPromotionStore.youtubeChannelId ?? '',
    },
    {
      enabled: !!videoPromotionStore.youtubeChannelId,
    },
  )

  const playlistsQuery = useGetApiVideoPromotionChannelsYoutubeChannelIdPlaylists(
    videoPromotionStore.youtubeChannelId ?? '',
    {
      query: {
        enabled: !!videoPromotionStore.youtubeChannelId,
      },
    },
  )
  const playlists = useMemo(
    () =>
      (playlistsQuery.data?.body.playlists ?? []).map(b => ({
        value: b.id,
        label: b.title,
      })),
    [playlistsQuery.data],
  )

  const infoCardTypes = useMemo(
    () =>
      VideoPromotionHelper.INFOCARDS_TYPES.map(it => ({
        value: it,
        label: t(`pages.videoPromotion.infoCards.types.${it}`),
      })),
    [t],
  )
  const isDisabled = useMemo(
    () =>
      playlistsQuery.isLoading ||
      (selectedType === 'playlist' && typeof form.values.playlistId === 'undefined') ||
      (selectedType === 'video' && typeof form.values.videoId === 'undefined'),
    [playlistsQuery.isLoading, selectedType, form.values],
  )

  const onEdit = useCallback(() => {
    const { infoCardsDraft, setInfoCardsDraft } = useVideoPromotionStore.getState()
    const infocard = infoCardsDraft.find(ic => ic.id === infocardId)
    if (!infocard) {
      onClose()
      return
    }
    const targetId = selectedType === 'video' ? form.values.videoId : form.values.playlistId
    if (!targetId) {
      return
    }
    const title =
      selectedType === 'video'
        ? form.values.videoTitle
        : playlists.find(p => p.value === targetId)?.label

    setInfoCardsDraft(
      produce(infoCardsDraft, draft => {
        const card = draft.find(ic => ic.id === infocard.id)
        if (card) {
          card.target = { type: selectedType, targetId, title }
        }
      }),
    )
    onClose()
  }, [infocardId, selectedType, form.values, onClose, playlists])

  return (
    <form onSubmit={form.onSubmit(onEdit)}>
      <Stack>
        <SimpleGrid cols={2}>
          <Select
            label={t('_generics.type')}
            data={infoCardTypes}
            value={selectedType}
            onChange={t => {
              t && setSelectedType(t as IInfocardTypes)
              form.setValues({
                playlistId: undefined,
                videoId: undefined,
                videoTitle: undefined,
              })
            }}
          />
          <Flex h="100%" justify="center" align="center">
            <Text c="dimmed" size="sm" fs="italic">
              {t('_generics.youtubeChannel')}: {youtubeChannelQuery.data?.name}
            </Text>
          </Flex>
        </SimpleGrid>
        <Divider w="80%" my="md" />
        <SimpleGrid cols={2}>
          {selectedType === 'video' && (
            <PromotionTargetVideoSelect
              label={t('pages.videoPromotion.videoName')}
              value={form.values.videoId}
              onChange={video => form.setValues({ videoId: video?.id, videoTitle: video?.title })}
            />
          )}
          {selectedType === 'playlist' && (
            <Select
              label={t('pages.videoPromotion.playlistName')}
              data={playlists}
              searchable
              nothingFoundMessage={t('pages.videoPromotion.noPlaylist')}
              disabled={!videoPromotionStore.youtubeChannelId}
              {...form.getInputProps('playlistId')}
            />
          )}
        </SimpleGrid>
        <Group justify="right" mt="xl">
          <Button variant="subtle" onClick={() => onClose()}>
            {t('_generics.cancel')}
          </Button>
          <Button type="submit" variant="filled" disabled={isDisabled}>
            {t('_generics.save')}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
